import React, { Suspense, lazy } from "react";

import { Provider } from "mobx-react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ConfigProvider, Spin } from "antd";

import { AppStore } from "./stores/AppStore";
import { brand } from "./theme/brands";
import AppUrlListener from "./native/AppUrlListener";
import PublicLayout from "./components/layout/PublicLayout";
import AdminLayout from "./components/layout/AdminLayout";
import RequireAuth from "./components/auth/RequireAuth";
import Landing from "./views/landing/Landing";
import Registration from "./views/registration/Registration";
import ThankYou from "./views/registration/ThankYou";
import Privacy from "./views/Privacy";
import AdminLogin from "./views/admin/AdminLogin";
import NotFound from "./views/NotFound";

// The admin area ships as its own chunk — public visitors never download it.
const AdminManage = lazy(() => import("./views/admin/AdminManage"));
const AdminEvents = lazy(() => import("./views/admin/events/Events"));
const Billing = lazy(() => import("./views/admin/billing/Billing"));
const Coupons = lazy(() => import("./views/admin/coupons/Coupons"));
const Users = lazy(() => import("./views/admin/users/Users"));
const Clients = lazy(() => import("./views/admin/clients/Clients"));

const appStore = AppStore.getInstance();

const suspenseFallback = (
  <div className="flex justify-center py-24">
    <Spin size="large" />
  </div>
);

function App() {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: brand.colors.primary,
          colorLink: brand.colors.primary,
          colorInfo: brand.colors.primary,
          colorTextBase: brand.colors.ink,
          colorBgLayout: brand.colors.layoutBg,
          borderRadius: 8,
          fontFamily: "'Inter Variable', system-ui, -apple-system, sans-serif",
          fontSize: 14,
          controlHeight: 40,
        },
        components: {
          Button: { fontWeight: 600, controlHeight: 40, controlHeightSM: 32 },
          Table: { cellPaddingBlockSM: 8 },
          Modal: { borderRadiusLG: 12 },
          Radio: { radioSize: 18 },
        },
      }}
    >
      <Provider store={appStore}>
        <BrowserRouter>
          <AppUrlListener />
          <Suspense fallback={suspenseFallback}>
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Landing />} />
                <Route path="/register/:eventId" element={<Registration store={appStore} />} />
                <Route path="/thankyou" element={<ThankYou />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="*" element={<NotFound />} />
              </Route>
              <Route path="/admin" element={<AdminLogin store={appStore} />} />
              <Route
                element={
                  <RequireAuth>
                    <AdminLayout />
                  </RequireAuth>
                }
              >
                <Route path="/admin/manage" element={<AdminManage />} />
                <Route path="/admin/events" element={<AdminEvents />} />
                <Route path="/admin/billing" element={<Billing />} />
                <Route path="/admin/coupons" element={<Coupons />} />
                <Route path="/admin/users" element={<Users />} />
                <Route path="/admin/clients" element={<Clients />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
      </Provider>
    </ConfigProvider>
  );
}

export default App;
